/**
 * The top of a business's bid: a proposal, not a homeowner's quote.
 *
 * Owner, Oct 19 2026: "the commercial bidding side should be way cooler." A
 * property manager reads this on a laptop between two other proposals, so it
 * reads like one: a letterhead that says what it is, who it is for and when;
 * the prices as tiles — a visit, a quarter, one time — with what that comes
 * to over a year; the first visit; and the three promises a business asks
 * about before the price (a photo report after every visit, net terms with
 * the PO on every invoice, insured with a COI on request). The maths is the
 * page's (QuotePublic) — this only draws it.
 */
import React from 'react';
import { Camera, Receipt, ShieldCheck, Calendar, PenLine } from 'lucide-react';
import { money as moneyCents } from '../utils/format';

import { H1 } from './public';
// Business prices are whole dollars on the proposal ($275, not $275.00); cents only when there are some.
const money = (n: number) => (Math.abs(n - Math.round(n)) < 0.005 ? `$${Math.round(n).toLocaleString('en-US')}` : moneyCents(n));


export interface CommercialFacts {
  company: string; site: string | null; terms: number; po: string | null;
  contact?: string | null; site_contact?: string | null; sites?: number; reports?: boolean;
}

const longDay = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : null);
const EVERY: Record<string, { words: string; perYear: number }> = {
  weekly: { words: 'every week', perYear: 52 },
  biweekly: { words: 'every other week', perYear: 26 },
  monthly: { words: 'every 4 weeks', perYear: 13 },
};

export default function BusinessProposal({
  facts, title, quoteNo, createdAt, frequency, visitTotal, monthlyTotal, quarterlyTotal, otherCadenceTotal, oneTimeTotal,
  firstVisit, firstVisitWords, quarterlyWords, signedName, signedAt, contract,
}: {
  facts: CommercialFacts; title: string; quoteNo: string; createdAt?: string | null; frequency?: string | null;
  visitTotal: number; monthlyTotal: number; quarterlyTotal: number; otherCadenceTotal: number; oneTimeTotal: number;
  firstVisit?: string | null; firstVisitWords?: string | null; quarterlyWords?: string | null;
  signedName?: string | null; signedAt?: string | null;
  // A monthly-contract bid (services/contractBid): "$X a month" and the term, with the sales tax on each monthly bill.
  contract?: { monthly: number; months: number; tax_pct: number; start: string | null } | null;
}) {
  const every = frequency ? EVERY[frequency] : null;
  const tiles: { label: string; value: string; note?: string }[] = [];
  if (contract) {
    tiles.push({ label: 'A month', value: money(contract.monthly), note: contract.tax_pct > 0 ? `plus ${contract.tax_pct}% sales tax` : undefined });
    tiles.push({ label: `${contract.months} months`, value: money(contract.monthly * contract.months), note: 'equal monthly payments' });
  }
  if (!contract && visitTotal > 0) tiles.push({ label: 'A visit', value: money(visitTotal), note: every ? every.words : undefined });
  if (!contract && monthlyTotal > 0) tiles.push({ label: 'A month', value: money(monthlyTotal) });
  if (!contract && quarterlyTotal > 0) tiles.push({ label: 'A quarter', value: money(quarterlyTotal), note: 'four times a year' });
  if (!contract && otherCadenceTotal > 0) tiles.push({ label: 'On its own schedule', value: money(otherCadenceTotal) });
  if (!contract && oneTimeTotal > 0) tiles.push({ label: 'One time', value: money(oneTimeTotal), note: visitTotal > 0 ? 'with the first visit' : undefined });
  // What a year comes to — only when the rhythm is known, and only the recurring part (the one-time work is said once).
  const year = (every ? visitTotal * every.perYear : 0) + monthlyTotal * 12 + quarterlyTotal * 4;
  const recurring = visitTotal > 0 || monthlyTotal > 0 || quarterlyTotal > 0;
  // The column is UTC; the day on the letterhead is Austin's.
  const prepared = createdAt ? new Date(String(createdAt).replace(' ', 'T') + (/[zZ]|[+-]\d\d(:?\d\d)?$/.test(String(createdAt)) ? '' : 'Z')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' }) : null;
  const site = facts.site && facts.site.trim().toLowerCase() !== facts.company.trim().toLowerCase() ? facts.site : null;

  return (
    <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
      {/* Letterhead */}
      <div className="px-5 pt-5 pb-4 border-b-2 border-brand-100">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">Service proposal</p>
          <p className="text-[13px] font-semibold text-gray-600 text-right tabular-nums">{quoteNo}{prepared ? ` · ${prepared}` : ''}</p>
        </div>
        <H1 className="mt-1">{title}</H1>
        <p className="text-[15px] text-gray-700 mt-2">
          Prepared for <span className="font-bold text-gray-900">{facts.company}</span>
          {site ? <> · <span className="font-semibold text-gray-900">{site}</span></> : null}
          {facts.sites && facts.sites > 1 ? <span className="text-gray-600"> · {facts.site ? 'one of' : ''} {facts.sites} sites</span> : null}
        </p>
        {(facts.contact || facts.site_contact) && (
          <p className="text-[14px] text-gray-600 mt-0.5">
            {facts.contact ? `Walked with ${facts.contact}` : ''}{facts.contact && facts.site_contact && facts.site_contact !== facts.contact ? ' · ' : ''}
            {facts.site_contact && facts.site_contact !== facts.contact ? `Site contact ${facts.site_contact}` : ''}
          </p>
        )}
      </div>

      {/* The price as tiles */}
      {tiles.length > 0 && (
        <div className="px-5 py-5 bg-brand-600 text-black">
          <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Your price</p>
          <div className={`mt-3 grid gap-3 ${tiles.length >= 3 ? 'grid-cols-3' : tiles.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {tiles.map(t => (
              <div key={t.label} className="min-w-0">
                <p className="text-[12px] sm:text-[13px] font-black uppercase tracking-wide text-black/70 truncate">{t.label}</p>
                <p className={`font-black tracking-tight tabular-nums leading-none mt-1 ${tiles.length >= 3 ? 'text-2xl sm:text-4xl' : 'text-4xl sm:text-5xl'}`}>{t.value}</p>
                {t.note && <p className="text-[13px] font-semibold text-black/80 mt-1 leading-snug">{t.note}</p>}
              </div>
            ))}
          </div>
          {contract ? (
            <>
              <p className="mt-4 pt-3 border-t border-black/20 text-[15px] font-bold">
                {contract.months}-month agreement, billed in equal monthly payments.{contract.tax_pct > 0 ? ` Texas sales tax (${contract.tax_pct}%) is added to each monthly bill.` : ''}
              </p>
              <p className="text-[15px] font-medium text-black/85 mt-2">
                {contract.start ? `Starts ${longDay(contract.start)}.` : 'The start date and the service calendar are confirmed in writing once you sign.'}
              </p>
            </>
          ) : recurring && year > 0 && (
            <p className="mt-4 pt-3 border-t border-black/20 text-[15px] font-bold">
              About {money(Math.round(year))} a year{oneTimeTotal > 0 ? `, plus ${money(oneTimeTotal)} once` : ''}.
            </p>
          )}
          {!contract && quarterlyWords && <p className="text-[15px] font-semibold text-black/85 mt-2">{quarterlyWords}</p>}
          {contract ? null : firstVisitWords
            ? <p className="text-[15px] font-medium text-black/85 mt-2">{firstVisitWords}</p>
            : firstVisit && <p className="text-[15px] font-medium text-black/85 mt-2">First visit {longDay(firstVisit)}{every ? `, then ${every.words}` : ''}.</p>}
        </div>
      )}

      {/* The three things a business asks before the price */}
      <ul className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[14px] text-gray-800">
        {facts.reports !== false && (
          <li className="flex items-start gap-2">
            <Camera className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden />
            <span><b className="text-gray-900">Photo report after every visit</b><br />emailed to your site contact the same day.</span>
          </li>
        )}
        <li className="flex items-start gap-2">
          <Receipt className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden />
          <span><b className="text-gray-900">Net {facts.terms}, {contract ? 'billed monthly' : 'billed month-end'}</b><br />{facts.po ? `PO ${facts.po} on every invoice.` : 'one invoice a month, your PO on it.'}</span>
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" aria-hidden />
          <span><b className="text-gray-900">Insured</b><br />certificate of insurance and W-9 on request.</span>
        </li>
      </ul>

      {signedName && (
        <p className="px-5 pb-4 -mt-1 text-[14px] text-emerald-800 font-semibold inline-flex items-center gap-1.5">
          <PenLine className="h-4 w-4" aria-hidden /> Signed by {signedName}{signedAt ? ` on ${new Date(String(signedAt).replace(' ', 'T') + (/[zZ]|[+-]\d\d(:?\d\d)?$/.test(String(signedAt)) ? '' : 'Z')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' })}` : ''}
        </p>
      )}
      {!signedName && !tiles.length && (
        <p className="px-5 pb-4 text-[14px] text-gray-600 inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" aria-hidden /> Prices are being put together.</p>
      )}
    </section>
  );
}
